import React, { useContext, useEffect, useState } from 'react';
import { ProgressBar, Alert, Modal, Button } from 'react-bootstrap';
import { Redirect, useHistory } from 'react-router';
import { makeApiRequests } from '../helpers/api';
import { toast } from 'react-toastify';
import { ENDPOINTS } from '../helpers/constants';
import { validateEmail } from '../helpers/global';
import CircularProgressBar from '../components/common/circular-progress';
import { LocalizeContext } from 'react-locale-language';
import LanguagePicker from '../components/common/LanguagePicker';
import { UserContext } from '../context/UserContext';
import { Link, useLocation } from 'react-router-dom';
import { BrandAside, LabeledInputField, authSubmitButtonClass, authLinkClass } from '../components/common/authUi';

const formStackClass = 'flex flex-col gap-3 md:gap-5 lg:gap-6';

const emptyLoginForm = { email: '', password: '' };
const emptyForgotForm = { email: '' };

/* FormGenerator used uncontrolled inputs; LabeledInputField needs React state — grouped per flow below. */
function ForgotPasswordModal({
  show,
  onHide,
  submitting,
  email,
  emailError,
  apiError,
  onEmailChange,
  onSubmit
}) {
  const { translate } = useContext(LocalizeContext);
  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>{translate('forgot_password')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        {!submitting ? (
          <div className="fade-in px-4 pb-4 pt-2">
            <form className="flex flex-col gap-4 md:gap-5" onSubmit={onSubmit} noValidate>
              <LabeledInputField
                id="fp-email"
                label={translate('email')}
                type="email"
                autoComplete="email"
                value={email}
                error={emailError}
                onValueChange={onEmailChange}
              />
              <button type="submit" className={authSubmitButtonClass}>
                {translate('get_reset_email')}
              </button>
            </form>
            {apiError && (
              <Alert className="mt-3 text-sm" variant="danger">
                {apiError}
              </Alert>
            )}
          </div>
        ) : (
          <div className="p-5">
            <div className="p-5 text-center">
              <CircularProgressBar size={5} />
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

function LoginForm({
  loginValues,
  loginErrors,
  submitting,
  onLoginFieldChange,
  onLoginSubmit,
  onOpenForgotModal,
  onGoogleAuthClick,
  forgotPasswordModalProps
}) {
  const { translate } = useContext(LocalizeContext);
  return (
    <div className="fade-in">
      <ForgotPasswordModal {...forgotPasswordModalProps} />

      <form className={formStackClass} onSubmit={onLoginSubmit} noValidate>
        <LabeledInputField
          id="login-email"
          label={translate('email')}
          type="email"
          autoComplete="email"
          value={loginValues.email}
          error={loginErrors.email}
          onValueChange={v => onLoginFieldChange('email', v)}
          disabled={submitting}
        />
        <LabeledInputField
          id="login-password"
          label={translate('password')}
          type="password"
          autoComplete="current-password"
          value={loginValues.password}
          error={loginErrors.password}
          onValueChange={v => onLoginFieldChange('password', v)}
          disabled={submitting}
        />
        <button type="submit" className={authSubmitButtonClass} disabled={submitting}>
          {translate('login')}
        </button>
      </form>

      <p
        className={`mt-3 cursor-pointer text-end ${authLinkClass} ${
          submitting ? 'pointer-events-none text-muted' : ''
        }`}
        onClick={() => {
          if (submitting) return;
          onOpenForgotModal();
        }}
      >
        {translate('forgot_password')}
      </p>

      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-brand-navy/12" />
        <span className="text-sm text-gray-500">OR</span>
        <div className="h-px flex-1 bg-brand-navy/12" />
      </div>

      <div className="mb-6">
        <Button
          variant="outline-secondary"
          onClick={onGoogleAuthClick}
          disabled={submitting}
          className="flex min-h-control w-full items-center justify-center gap-3 rounded-2xl border-brand-navy/20 bg-white text-sm font-semibold text-brand-navy hover:bg-brand-surface"
        >
          <img src="/google-logo.svg" alt="Google Logo" />
          Sign in with Google
        </Button>
      </div>

      <div className="text-center">
        <a
          className={`${authLinkClass} ${submitting ? 'pointer-events-none text-muted' : ''}`}
          href="mailto:office@ethica.finance"
        >
          {translate('need_help_email_us')}
        </a>
      </div>
    </div>
  );
}

const Login = () => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [showForgotPassModal, setShowForgotPassModal] = useState(false);
  const [forgotPasswordSubmitting, setForgotPasswordSubmitting] = useState(false);
  const [forceHideForm, setForceHideForm] = useState(true);
  const [forgotModalApiError, setForgotModalApiError] = useState('');

  const [loginValues, setLoginValues] = useState(emptyLoginForm);
  const [loginErrors, setLoginErrors] = useState(emptyLoginForm);

  const [forgotValues, setForgotValues] = useState(emptyForgotForm);
  const [forgotErrors, setForgotErrors] = useState(emptyForgotForm);

  const history = useHistory();
  const { translate } = useContext(LocalizeContext);
  const { isUserLoggedIn, login: loginLocally } = useContext(UserContext);

  useEffect(() => {
    setApiError('');
    const timer = setTimeout(() => setForceHideForm(false), 300);
    return () => clearTimeout(timer);
  }, []);

  if (isUserLoggedIn) {
    return <Redirect from="/login" to="/" />;
  }

  const handleLoginChange = (key, val) => {
    setLoginValues(prev => ({ ...prev, [key]: val }));
    if (loginErrors[key]) setLoginErrors(prev => ({ ...prev, [key]: '' }));
  };

  const handleForgotChange = (key, val) => {
    setForgotValues(prev => ({ ...prev, [key]: val }));
    if (forgotErrors[key]) setForgotErrors(prev => ({ ...prev, [key]: '' }));
  };

  const auth = async ({ authEndpoint, requestBody }) => {
    setSubmitting(true);
    setApiError('');
    try {
      const { error } = await makeApiRequests({
        endpoint: authEndpoint,
        requestBody: requestBody
      });

      if (error) {
        setApiError(error);
        return;
      }

      loginLocally();
    } catch (authErr) {
      setApiError('Something went wrong! Please try again');
      console.log(authErr);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoginSubmit = e => {
    e.preventDefault();
    setApiError('');
    const newErrors = {
      email: !loginValues.email.trim() || !validateEmail(loginValues.email) ? translate('invalid_email_short') : '',
      password: !loginValues.password.trim() ? translate('field_required') : ''
    };

    if (Object.values(newErrors).some(Boolean)) {
      setLoginErrors(newErrors);
      return;
    }
    setLoginErrors(emptyLoginForm);
    auth({
      authEndpoint: ENDPOINTS.LOGIN,
      requestBody: { email: loginValues.email, password: loginValues.password }
    });
  };

  const handleForgotEmailSubmit = async e => {
    e.preventDefault();
    setForgotModalApiError('');
    if (!forgotValues.email.trim() || !validateEmail(forgotValues.email)) {
      setForgotErrors({ email: translate('invalid_email_short') });
      return;
    }
    setForgotErrors(emptyForgotForm);
    setForgotPasswordSubmitting(true);
    try {
      const { error } = await makeApiRequests({
        endpoint: ENDPOINTS.FORGOT_PASSWORD,
        requestBody: { email: forgotValues.email }
      });

      if (error) {
        setForgotModalApiError(error);
        return;
      }
      toast.success('A link to reset password has been sent to your email!');
      onForgotPasswordModalHide();
    } catch (err) {
      toast.error('Something went wrong! Please try again');
      console.log(err);
    } finally {
      setForgotPasswordSubmitting(false);
    }
  };

  const onForgotPasswordModalHide = () => {
    setShowForgotPassModal(false);
    setForgotModalApiError('');
    setForgotValues(emptyForgotForm);
    setForgotErrors(emptyForgotForm);
  };

  const onGoogleAuthClick = () => {
    window.location.href = ENDPOINTS.GOOGLE_AUTH;
  };

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const oauth = searchParams.get('oauth');

  useEffect(() => {
    if (oauth === 'success') {
      loginLocally();
      history.push('/');
    } else if (oauth === 'error') {
      setApiError('Invalid Google Credentials.');
      history.push('/login');
    }
  }, [oauth, loginLocally, history]);

  const mainHeading = translate('sign_in_to_continue');

  return (
    <div className="flex min-h-dvh w-full flex-col overflow-x-hidden bg-brand-surface font-sans text-brand-navy lg:min-h-screen lg:flex-row lg:overflow-hidden">
      <BrandAside titleKey="login_brand_headline" taglineKey="login_brand_tagline" />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:min-h-screen">
        <header className="flex w-full flex-wrap items-center justify-end gap-x-3 gap-y-3 border-b border-brand-navy/6 bg-brand-surface px-4 py-3.5 sm:gap-x-5 sm:px-[clamp(1.25rem,4vw,3rem)] sm:py-5">
          <div className="flex flex-shrink-0 items-center gap-4 sm:gap-6">
            <LanguagePicker />
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col items-stretch justify-start px-4 py-6 sm:justify-center sm:px-[clamp(1.25rem,5vw,3rem)] sm:py-10 md:py-12 md:pb-16">
          <div className="w-full max-w-auth-form self-center">
            {!forceHideForm ? (
              <>
                <p className="mb-2 text-sm text-gray-500 sm:mb-3">{translate('welcome_to_ETICA_financials')}</p>
                <h1 className="mb-8 font-serif text-3xl font-semibold leading-tight tracking-tight sm:mb-10 sm:text-4xl">
                  {mainHeading}
                </h1>

                <LoginForm
                  loginValues={loginValues}
                  loginErrors={loginErrors}
                  submitting={submitting}
                  onLoginFieldChange={handleLoginChange}
                  onLoginSubmit={handleLoginSubmit}
                  onOpenForgotModal={() => setShowForgotPassModal(true)}
                  onGoogleAuthClick={onGoogleAuthClick}
                  forgotPasswordModalProps={{
                    show: showForgotPassModal,
                    onHide: onForgotPasswordModalHide,
                    submitting: forgotPasswordSubmitting,
                    email: forgotValues.email,
                    emailError: forgotErrors.email,
                    apiError: forgotModalApiError,
                    onEmailChange: v => handleForgotChange('email', v),
                    onSubmit: handleForgotEmailSubmit
                  }}
                />

                {submitting && (
                  <ProgressBar
                    className="mt-5"
                    striped
                    animated
                    variant="dark"
                    now={100}
                    label={`${translate('logging_in')}...`}
                  />
                )}
                {apiError && (
                  <Alert className="mt-4 text-sm" variant="danger">
                    {apiError}
                  </Alert>
                )}

                {/* // TODO: prevent public signup for now */}
                {/* <p className="mt-8 text-center text-sm sm:mt-10">
                  <Link
                    to="/signup"
                    className={`${authLinkClass} ${submitting ? 'pointer-events-none text-muted' : ''}`}
                  >
                    {translate('public_signup')}
                  </Link>
                </p> */}
              </>
            ) : (
              <div
                className="flex w-full grow items-center justify-center self-center py-5"
                style={{ minHeight: '40vh' }}
              >
                <CircularProgressBar size={5} />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Login;
